import * as React from "react";

// ---------------------------------------------------------------------------
// AdminPageHeader — .pgh (distinct from user-facing .hdr PageHeader)
// ---------------------------------------------------------------------------
export interface AdminPageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}
export function AdminPageHeader({
  title,
  description,
  actions,
  className = "",
}: AdminPageHeaderProps) {
  return (
    <div className={["pgh", className].filter(Boolean).join(" ")}>
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PostureBar — runtime-status strip (.posture)
// ---------------------------------------------------------------------------
export interface PostureBarProps {
  children: React.ReactNode;
  className?: string;
}
export function PostureBar({ children, className = "" }: PostureBarProps) {
  return <div className={["posture", className].filter(Boolean).join(" ")}>{children}</div>;
}

export interface PostureHeadProps {
  label: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  className?: string;
}
export function PostureHead({ label, title, sub, className = "" }: PostureHeadProps) {
  return (
    <div className={["pos-head", className].filter(Boolean).join(" ")}>
      <span className="lbl">{label}</span>
      <span className="ttl">{title}</span>
      {sub && <span className="sub">{sub}</span>}
    </div>
  );
}

export type PostureDot = "ok" | "mute" | "warn";

export interface PostureCellProps {
  label: React.ReactNode;
  value: React.ReactNode;
  dot?: PostureDot;
  sub?: React.ReactNode;
  monoSub?: boolean;
  className?: string;
}
export function PostureCell({ label, value, dot, sub, monoSub, className = "" }: PostureCellProps) {
  return (
    <div className={["pos-cell", className].filter(Boolean).join(" ")}>
      <div className="lbl">{label}</div>
      <div className="val">
        {dot && <span className={`dot ${dot}`} />}
        {value}
      </div>
      {sub && <div className={["sub", monoSub && "mono"].filter(Boolean).join(" ")}>{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionHeader — .sec-hdr
// ---------------------------------------------------------------------------
export interface SectionHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
}
export function SectionHeader({ title, description, className = "" }: SectionHeaderProps) {
  return (
    <div className={["sec-hdr", className].filter(Boolean).join(" ")}>
      <h2>{title}</h2>
      {description && <p>{description}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdminGrid — .admin-grid / .admin-grid-2
// ---------------------------------------------------------------------------
export interface AdminGridProps {
  cols?: 2 | 3 | "auto";
  children: React.ReactNode;
  className?: string;
}
export function AdminGrid({ cols = 3, children, className = "" }: AdminGridProps) {
  const base =
    cols === "auto" ? "admin-grid-auto" : cols === 2 ? "admin-grid-2" : "admin-grid";
  return <div className={[base, className].filter(Boolean).join(" ")}>{children}</div>;
}

// ---------------------------------------------------------------------------
// AdminFormGrid — two-column form layout for admin settings
// ---------------------------------------------------------------------------
export interface AdminFormGridProps {
  children: React.ReactNode;
  className?: string;
}
export function AdminFormGrid({ children, className = "" }: AdminFormGridProps) {
  return <div className={["admin-form-grid", className].filter(Boolean).join(" ")}>{children}</div>;
}

// ---------------------------------------------------------------------------
// KvTable + KvRow — key-value summary rows
// ---------------------------------------------------------------------------
export interface KvTableProps {
  children: React.ReactNode;
  className?: string;
}
export function KvTable({ children, className = "" }: KvTableProps) {
  return <div className={["kv", className].filter(Boolean).join(" ")}>{children}</div>;
}

export interface KvRowProps {
  label: React.ReactNode;
  value: React.ReactNode;
  masked?: boolean;
  className?: string;
}
export function KvRow({ label, value, masked, className = "" }: KvRowProps) {
  return (
    <div className={["kv-row", className].filter(Boolean).join(" ")}>
      <span className="k">{label}</span>
      <span className={["v", masked && "masked"].filter(Boolean).join(" ")}>{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdminBadge — .admin-badge
// ---------------------------------------------------------------------------
export type AdminBadgeTone = "ok" | "scr" | "warn" | "fail" | "mute";

export interface AdminBadgeProps {
  tone?: AdminBadgeTone;
  children: React.ReactNode;
  className?: string;
}
export function AdminBadge({ tone = "mute", children, className = "" }: AdminBadgeProps) {
  return (
    <span className={["admin-badge", `b-${tone}`, className].filter(Boolean).join(" ")}>
      <span className="d" />
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// AdminEmpty — empty state placeholder
// ---------------------------------------------------------------------------
export interface AdminEmptyProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
}
export function AdminEmpty({ icon, title, description, className = "" }: AdminEmptyProps) {
  return (
    <div className={["admin-empty", className].filter(Boolean).join(" ")}>
      {icon && <div className="ic">{icon}</div>}
      <div className="t">{title}</div>
      {description && <div className="s">{description}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdminAlert — warning banner
// ---------------------------------------------------------------------------
export interface AdminAlertProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}
export function AdminAlert({ icon, title, children, className = "" }: AdminAlertProps) {
  return (
    <div className={["admin-alert", className].filter(Boolean).join(" ")}>
      {icon && <div className="ic">{icon}</div>}
      <div className="body">
        <b>{title}</b>
        {children && <span>{children}</span>}
      </div>
    </div>
  );
}
